import React, { useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../hooks/ThemeContext';
import { CartContext } from '../../hooks/CartContext';
import { useProductById } from '../../hooks/useProducts';
import ProtectedImage from '../../components/common/ProtectedImage';
import LoadingSpinner from '../../components/globals/LoadingSpinner';
import SectionTitle from '../../components/common/SectionTitle';

const ProductDetails = () => {
  const { theme } = useContext(ThemeContext);
  const { addToCart } = useContext(CartContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const { product, loading, error, refetch } = useProductById(id);

  const handleAddToCart = async () => {
    const productId = product?.id_product ?? product?.id;
    if (!productId) return;
    await addToCart(productId, 1);
    // Actualiser le produit pour récupérer le stock mis à jour
    if (refetch) {
      await refetch();
    }
  };

  if (loading) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {error || 'Produit introuvable'}
          </div>
          <button
            onClick={() => navigate('/shop')}
            className="mt-6 px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  const title = product.name_product || product.name || 'Produit';
  const description = product.description_product || product.description || '';
  const price = product.price_product || product.price || 0;
  const rawStock = product.available_stock ?? product.stock ?? product.quantity_product ?? 0;
  const stock = rawStock === 0 ? 'Illimité' : rawStock;
  const saleType = product.sale_type || 'libre';

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <SectionTitle
          title={title}
          subtitle={saleType === 'unique' ? 'Exemplaire unique' : `Stock disponible: ${stock}`}
          align="left"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Image protégée */}
          <div className={`rounded-lg overflow-hidden ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
            <div className="aspect-video">
              <ProtectedImage
                imagePath={product.image ?? product.image_url ?? product.path_watermarked ?? product.path_image}
                alt={title}
                className="w-full h-full"
                objectFit="contain"
                showWatermark={true}
              />
            </div>
          </div>

          {/* Infos */}
          <div className={`rounded-lg p-6 ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
            <h2 className="text-2xl font-bold mb-3">{title}</h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {description}
            </p>

            <div className="flex items-center justify-between mb-6">
              <span className={`text-3xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                {price}€
              </span>
              <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {saleType === 'unique' ? 'Exemplaire unique' : `Stock: ${stock}`}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAddToCart}
                className="px-5 py-3 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 transition-colors"
              >
                Ajouter au panier
              </button>
              <button
                onClick={() => navigate('/shop')}
                className={`px-5 py-3 rounded-lg border font-medium transition-colors ${
                  isDark
                    ? 'border-white/20 text-white hover:bg-white/10'
                    : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                }`}
              >
                Retour boutique
              </button>
            </div>

            <div className={`mt-6 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              🔒 Image protégée par filigrane et lien expirable.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
